import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Paper,
  IconButton,
} from '@mui/material';
import { LockOutlined, CheckCircleOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { LoadingButton } from '@mui/lab';
import AppDelayedRender from '@tenant/core/App/Render/AppDelayedRender';
import useUpdateNetworkingConfiguration from 'redux/slices/containerApp/hooks/useUpdateNetworkingConfiguration';

const Networking = ({ 
  data, 
  appInstanceId, 
  containerAppId, 
  refetchDetail = () => {} 
}) => {
  const defaultData = {
    isAccessRestricted: false,
    whitelistedIPs: [], // Changed to array to store objects with Name and IP
  };

  const [networking, setNetworking] = useState(defaultData);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newIP, setNewIP] = useState('');
  const [newIPName, setNewIPName] = useState('');

  // API hook for updating networking configuration
  const { mutate: updateNetworkingConfig, isPending: isSaving } = useUpdateNetworkingConfiguration();

  // Initialize state with props data
  useEffect(() => {
    if (data) {
      // Convert API format to internal format
      let whitelistedIPsArray = [];
      if (data.whitelistedIPs && Array.isArray(data.whitelistedIPs)) {
        whitelistedIPsArray = data.whitelistedIPs.map(item => ({
          Name: item.Name, // Display name
          IP: item.IP // IP address
        }));
      }
      
      setNetworking({
        isAccessRestricted: data.isAccessRestricted || false,
        whitelistedIPs: whitelistedIPsArray,
      });
    }
  }, [data]);

  // Check if there are changes
  const isChanged = useMemo(() => {
    // Get current data for comparison (similar to ResourceAllocation tab)
    let currentWhitelistedIPs = [];
    if (data?.whitelistedIPs && Array.isArray(data.whitelistedIPs)) {
      currentWhitelistedIPs = data.whitelistedIPs;
    }

    const currentData = {
      isAccessRestricted: data?.isAccessRestricted || false,
      whitelistedIPs: currentWhitelistedIPs,
    };

    // Compare arrays by stringifying them for deep comparison
    const hasChanges = (
      networking.isAccessRestricted !== currentData.isAccessRestricted ||
      JSON.stringify(networking.whitelistedIPs) !== JSON.stringify(currentData.whitelistedIPs)
    );

    return hasChanges;
  }, [networking, data]);

  const handleNetworkingChange = (field, value) => {
    setNetworking(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const payload = {
      appInstanceId,
      containerAppId,
      IsNetworkRestrictionEnabled: networking.isAccessRestricted,
    };

    // Only include WhitelistIPs if restrict access is enabled
    if (networking.isAccessRestricted) {
      // Convert to API format: send both Name and IP
      payload.WhitelistIPs = networking.whitelistedIPs.map(ipObj => ({
        Name: ipObj.Name, // Display name
        IP: ipObj.IP // IP address
      }));
    }

    updateNetworkingConfig(payload, {
      onSuccess: () => {
        // Refetch the detail data to sync with server
        refetchDetail();
      },
    });
  };

  const handleReset = () => {
    // Convert original data to internal format
    let originalWhitelistedIPs = [];
    if (data?.whitelistedIPs && Array.isArray(data.whitelistedIPs)) {
      originalWhitelistedIPs = data.whitelistedIPs.map(item => ({
        Name: item.Name, // Display name
        IP: item.IP // IP address
      }));
    }

    const originalData = {
      isAccessRestricted: data?.isAccessRestricted || false,
      whitelistedIPs: originalWhitelistedIPs,
    };
    setNetworking(originalData);
  };

  const handleAddIP = () => {
    if (newIP.trim() && newIPName.trim()) {
      const newIPObject = {
        Name: newIPName.trim(), // Display name
        IP: newIP.trim(), // IP address
      };
      const updatedIPs = [...networking.whitelistedIPs, newIPObject];
      handleNetworkingChange('whitelistedIPs', updatedIPs);
      setNewIP('');
      setNewIPName('');
      setDialogOpen(false);
    }
  };

  const handleRemoveIP = (ipObjectToRemove) => {
    const updatedIPs = networking.whitelistedIPs.filter(ipObj => 
      ipObj.IP !== ipObjectToRemove.IP || ipObj.Name !== ipObjectToRemove.Name
    );
    handleNetworkingChange('whitelistedIPs', updatedIPs);
  };

  return (
    <AppDelayedRender>
      <Box sx={{ p: 2 }}>
        {isChanged && (
          <Box mb={3}>
            <Alert
              severity='info'
              action={
                <>
                  <LoadingButton
                    size='small'
                    color='error'
                    onClick={handleReset}
                  >
                    Reset
                  </LoadingButton>
                  <LoadingButton
                    size='small'
                    onClick={handleSave}
                    loading={isSaving}
                    disabled={isSaving}
                  >
                    Save Changes
                  </LoadingButton>
                </>
              }
            >
              You have changed networking settings. Do you want to update the changes?
            </Alert>
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Access Restriction Section */}
          <Grid item xs={12}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 2,
                  }}
                >
                  <LockOutlined sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                    Access Restriction
                  </Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={networking.isAccessRestricted}
                      onChange={(e) => handleNetworkingChange('isAccessRestricted', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body1">
                      Restrict Access: <strong>{networking.isAccessRestricted ? 'ON' : 'OFF'}</strong>
                    </Typography>
                  }
                />

                <Alert severity="info" sx={{ mt: 2 }}>
                  When ON, only whitelisted IP addresses can access this container app.
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          {/* Whitelisted IP Addresses Section - Only show when access restriction is ON */}
          {networking.isAccessRestricted && (
            <Grid item xs={12}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 3,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      pb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleOutlined sx={{ mr: 1, color: 'success.main' }} />
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                        Whitelisted IP Addresses
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setDialogOpen(true)}
                      size="small"
                    >
                      Add New IP Address
                    </Button>
                  </Box>

                  <TableContainer component={Paper} elevation={0}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Name</strong></TableCell>
                          <TableCell><strong>IP Address</strong></TableCell>
                          <TableCell width="100" align="center"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {networking.whitelistedIPs.length > 0 ? (
                          networking.whitelistedIPs.map((ipObj, index) => (
                            <TableRow key={index}>
                              <TableCell>{ipObj.Name}</TableCell>
                              <TableCell>{ipObj.IP}</TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveIP(ipObj)}
                                  title="Remove IP Address"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell align="center" colSpan={3} sx={{ py: 4, color: 'text.secondary' }}>
                              No whitelisted IP addresses configured
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Add IP Dialog */}
        <Dialog open={dialogOpen} onClose={() => {
          setDialogOpen(false);
          setNewIP('');
          setNewIPName('');
        }} maxWidth="sm" fullWidth>
          <DialogTitle>Add New IP Address</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={newIPName}
                  onChange={(e) => setNewIPName(e.target.value)}
                  placeholder="e.g., Office Network, Home Router"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="IP Address"
                  value={newIP}
                  onChange={(e) => setNewIP(e.target.value)}
                  placeholder="e.g., 10.0.1.25 or 192.168.1.0/24"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setDialogOpen(false);
              setNewIP('');
              setNewIPName('');
            }}>Cancel</Button>
            <Button 
              onClick={handleAddIP} 
              variant="contained"
              disabled={!newIP.trim() || !newIPName.trim()}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppDelayedRender>
  );
};

export default Networking;

Networking.propTypes = {
  data: PropTypes.object,
  appInstanceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containerAppId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refetchDetail: PropTypes.func,
};
